import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import { X, Calendar } from '@phosphor-icons/react';
import type { TimeGranularity } from '../../types/reportBuilder';

const GRANULARITIES: { value: TimeGranularity; label: string }[] = [
  { value: 'year', label: 'Year' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
];

export default function TimeGroupingPicker() {
  const { getDateColumns, timeGroupings, addTimeGrouping, removeTimeGrouping } = useReportBuilderStore();
  const dateColumns = getDateColumns();

  if (dateColumns.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Time Grouping</h3>

      {/* Active groupings */}
      {timeGroupings.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {timeGroupings.map(tg => {
            const col = dateColumns.find(c => c.refKey === tg.column);
            const label = col ? `${col.label} (${tg.granularity.charAt(0).toUpperCase() + tg.granularity.slice(1)})` : tg.column;
            return (
              <span
                key={`${tg.column}_${tg.granularity}`}
                className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-800"
              >
                <Calendar size={12} weight="bold" />
                {label}
                <button
                  onClick={() => removeTimeGrouping(tg.column)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-teal-200"
                >
                  <X size={10} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Date columns with granularity selectors */}
      <div className="space-y-1.5">
        {dateColumns.map(col => {
          const activeTg = timeGroupings.find(tg => tg.column === col.refKey);
          return (
            <div key={col.refKey} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-xs text-gray-700">{col.label}</span>
              <div className="flex gap-0.5">
                {GRANULARITIES.map(g => {
                  const isActive = activeTg?.granularity === g.value;
                  return (
                    <button
                      key={g.value}
                      onClick={() => {
                        if (isActive) {
                          removeTimeGrouping(col.refKey);
                        } else {
                          // Replace any existing grouping for this column
                          removeTimeGrouping(col.refKey);
                          addTimeGrouping({ column: col.refKey, granularity: g.value });
                        }
                      }}
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                        isActive
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {g.label.charAt(0)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
